import React, { Suspense, ComponentType } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * Loading component for lazy-loaded components
 */
interface LoadingComponentProps {
  message?: string;
}

const LoadingComponent: React.FC<LoadingComponentProps> = ({ message = 'Loading...' }) => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap={2}
    p={3}
    role="status"
    aria-live="polite"
    aria-label={message}
  >
    <CircularProgress size={40} aria-hidden="true" />
    <Typography variant="body1">{message}</Typography>
  </Box>
);

/**
 * Error boundary component for lazy-loaded components
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class LazyErrorBoundary extends React.Component<
  React.PropsWithChildren<{ fallback?: React.ComponentType<{ error?: Error }> }>,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren<{ fallback?: React.ComponentType<{ error?: Error }> }>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Lazy component loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback;
      if (FallbackComponent) {
        return <FallbackComponent error={this.state.error} />;
      }
      return (
        <Box p={2} textAlign="center">
          <Typography color="error">
            Failed to load component. Please try again.
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * Creates a lazy-loaded component with loading and error states
 */
export const createLazyComponent = <P extends object = {}>(
  importFunc: () => Promise<{ default: ComponentType<P> }>,
  loadingMessage?: string | null, // Allow null to disable loading state
  ErrorFallback?: ComponentType<{ error?: Error }>
): ComponentType<P> => {
  const LazyComponent = React.lazy(importFunc);

  return (props: P) => {
    // If loadingMessage is explicitly null or undefined, allow silent loading
    // The user requested to remove the "Loading..." text.
    // We'll render null as fallback so it shows the background until loaded.
    const fallback = loadingMessage ? (
      <LoadingComponent
        message={
          loadingMessage.startsWith('Loading')
            ? loadingMessage
            : `Loading ${loadingMessage}...`
        }
      />
    ) : null;

    return (
      <LazyErrorBoundary fallback={ErrorFallback}>
        <Suspense fallback={fallback}>
          <LazyComponent {...props} />
        </Suspense>
      </LazyErrorBoundary>
    );
  };
};